import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, SelectChangeEvent, Snackbar } from '@mui/material';
import { FileDownload as DownloadIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
  filterBranchLocations,
} from '../components/common/list-page';
import CommonPagination from '../components/common/Pagination';
import EmptyStateMessage from '../components/common/EmptyStateMessage';
import { ExportButton } from '../components/sales/orders/OrdersActionButtons/OrdersActionButtons.styles';
import SalesByPieceFilters from '../components/sales/sales-by-piece/SalesByPieceFilters';
import SalesByPieceTable from '../components/sales/sales-by-piece/SalesByPieceTable';
import SoldPieceDetailModal from '../components/sales/sales-by-piece/SoldPieceDetailModal';
import {
  emptySalesByPieceFilters,
  type SalesByPieceFiltersState,
} from '../components/sales/sales-by-piece/types';
import {
  soldItemsService,
  type SoldItemListRow,
  type SoldItemsFilterParams,
} from '../services/soldItems.service';
import { LocationsService } from '../api';
import { downloadSoldItemsCsv } from '../utils/soldItemsExport';
import { selectCurrentUser } from '../redux/slices/auth.slice';
import { useCanChangeInventoryLocation } from '../hooks/useCanChangeInventoryLocation';

const filtersToParams = (
  filters: SalesByPieceFiltersState,
  page: number,
  limit: number,
): SoldItemsFilterParams => {
  const params: SoldItemsFilterParams = { page, limit };
  if (filters.productCodeUnique.trim()) params.productCodeUnique = filters.productCodeUnique.trim();
  if (filters.categoryName.trim()) params.categoryName = filters.categoryName.trim();
  if (filters.saleDateFrom) params.saleDateFrom = filters.saleDateFrom;
  if (filters.saleDateTo) params.saleDateTo = filters.saleDateTo;
  if (filters.sellerSearch.trim()) params.sellerSearch = filters.sellerSearch.trim();
  if (filters.locationId) params.locationId = Number(filters.locationId);
  if (filters.hasCustomer === 'true') params.hasCustomer = true;
  if (filters.hasCustomer === 'false') params.hasCustomer = false;
  if (filters.customerSearch.trim()) params.customerSearch = filters.customerSearch.trim();
  if (filters.finalPriceMin.trim()) params.finalPriceMin = Number(filters.finalPriceMin);
  if (filters.finalPriceMax.trim()) params.finalPriceMax = Number(filters.finalPriceMax);
  if (filters.orderCode.trim()) params.orderCode = filters.orderCode.trim();
  if (filters.branchReceiptCode.trim()) {
    params.branchReceiptCode = filters.branchReceiptCode.trim();
  }
  if (filters.daysUntilSaleMin.trim()) {
    params.daysUntilSaleMin = Number(filters.daysUntilSaleMin);
  }
  if (filters.daysUntilSaleMax.trim()) {
    params.daysUntilSaleMax = Number(filters.daysUntilSaleMax);
  }
  return params;
};

const SalesByPiecePage: React.FC = () => {
  const currentUser = useSelector(selectCurrentUser) as {
    primaryLocationId?: number | null;
  } | null;
  const canChangeInventoryLocation = useCanChangeInventoryLocation();
  const isLocationLocked = Boolean(currentUser?.primaryLocationId) && !canChangeInventoryLocation;

  const initialLocationId =
    isLocationLocked && currentUser?.primaryLocationId ? String(currentUser.primaryLocationId) : '';

  const [draftFilters, setDraftFilters] = useState<SalesByPieceFiltersState>(() => ({
    ...emptySalesByPieceFilters(),
    locationId: initialLocationId,
  }));
  const [appliedFilters, setAppliedFilters] = useState<SalesByPieceFiltersState>(() => ({
    ...emptySalesByPieceFilters(),
    locationId: initialLocationId,
  }));
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [rows, setRows] = useState<SoldItemListRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Array<{ id: number; name: string; type?: string }>>(
    [],
  );
  const [pagination, setPagination] = useState({ currentPage: 1, itemsPerPage: 10 });
  const [detailItemId, setDetailItemId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [notice, setNotice] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!isLocationLocked || !currentUser?.primaryLocationId) return;
    const primaryId = String(currentUser.primaryLocationId);
    setDraftFilters((prev) =>
      prev.locationId === primaryId ? prev : { ...prev, locationId: primaryId },
    );
    setAppliedFilters((prev) =>
      prev.locationId === primaryId ? prev : { ...prev, locationId: primaryId },
    );
  }, [isLocationLocked, currentUser?.primaryLocationId]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await soldItemsService.list(
        filtersToParams(appliedFilters, pagination.currentPage, pagination.itemsPerPage),
      );
      setRows(res.data);
      setTotalItems(res.total);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al cargar las ventas por pieza';
      setError(msg);
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const res = await LocationsService.locationsControllerFindAll();
        const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
        setLocations(
          (list as Array<{ id: number; name: string; type?: string }>).map((l) => ({
            id: l.id,
            name: l.name,
            type: l.type,
          })),
        );
      } catch {
        setLocations([]);
      }
    };
    void loadLocations();
  }, []);

  const branchLocations = useMemo(() => filterBranchLocations(locations), [locations]);

  const handleSearch = () => {
    setAppliedFilters(draftFilters);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleReset = () => {
    setDraftFilters((prev) => {
      const next = { ...emptySalesByPieceFilters(), locationId: prev.locationId };
      setAppliedFilters(next);
      return next;
    });
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleLocationScopeChange = useCallback(
    (payload: { locationId: string; locationName: string }) => {
      setDraftFilters((prev) => ({ ...prev, locationId: payload.locationId }));
      setAppliedFilters((prev) => ({ ...prev, locationId: payload.locationId }));
      setPagination((p) => ({ ...p, currentPage: 1 }));
      setNotice({
        open: true,
        message: `Mostrando piezas de ${payload.locationName}`,
        severity: 'info',
      });
    },
    [],
  );

  const handleClearAppliedField = useCallback((field: keyof SalesByPieceFiltersState) => {
    setDraftFilters((prev) => ({ ...prev, [field]: '' }));
    setAppliedFilters((prev) => ({ ...prev, [field]: '' }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
  }, []);

  const handleExport = async () => {
    setExportBusy(true);
    try {
      const res = await soldItemsService.list(filtersToParams(appliedFilters, 1, 50000));
      downloadSoldItemsCsv(res.data);
      setNotice({
        open: true,
        message:
          res.data.length === 1
            ? 'Se exportó 1 pieza vendida.'
            : `Se exportaron ${res.data.length} piezas (filtros aplicados).`,
        severity: 'success',
      });
    } catch {
      setNotice({
        open: true,
        message: 'No se pudo exportar.',
        severity: 'error',
      });
    } finally {
      setExportBusy(false);
    }
  };

  const handleView = (row: SoldItemListRow) => {
    setDetailItemId(row.salesOrderItemId);
    setDetailOpen(true);
  };

  const handlePageChange = (page: number) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<unknown>) => {
    setPagination({ currentPage: 1, itemsPerPage: Number(event.target.value) });
  };

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          <ExportButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            onClick={() => void handleExport()}
            disabled={exportBusy || loading}
          >
            {exportBusy ? 'Exportando…' : 'Exportar'}
          </ExportButton>
        </ListPageToolbar>

        <ListPageFiltersSection>
          <SalesByPieceFilters
            filters={draftFilters}
            setFilters={setDraftFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            onToggleMoreFilters={() => setShowMoreFilters((v) => !v)}
            onSearch={handleSearch}
            onReset={handleReset}
            locations={branchLocations}
            isLoading={loading}
            isLocationLocked={isLocationLocked}
            onLocationScopeChange={handleLocationScopeChange}
          />
        </ListPageFiltersSection>

        {error && (
          <Box sx={{ py: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && rows.length === 0 && (
          <EmptyStateMessage message="No hay piezas vendidas con estos filtros." />
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <SalesByPieceTable data={rows} onView={handleView} />
            <Box sx={{ mt: 3 }}>
              <CommonPagination
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          </>
        )}
      </ListPageMain>

      <SoldPieceDetailModal
        open={detailOpen}
        salesOrderItemId={detailItemId}
        onClose={() => {
          setDetailOpen(false);
          setDetailItemId(null);
        }}
      />

      <Snackbar
        open={notice.open}
        autoHideDuration={notice.severity === 'info' ? 3200 : 5000}
        onClose={() => setNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: notice.severity === 'info' ? 'center' : 'right',
        }}
      >
        <Alert
          severity={notice.severity}
          onClose={() => setNotice((n) => ({ ...n, open: false }))}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            ...(notice.severity === 'info'
              ? {
                  boxShadow: '0 8px 24px rgba(6, 182, 212, 0.25)',
                  background: 'linear-gradient(to right, #06b6d4, #10b981)',
                  color: '#fff',
                  '& .MuiAlert-icon': { color: '#fff' },
                }
              : {}),
          }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};

export default SalesByPiecePage;
